// src/pages/checkout/ShoppingCart.jsx
import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { useCheckout } from "./CheckoutContext";
import { useNavigate } from "react-router-dom";

const yen = (n) => `${n.toLocaleString()} 円`;

export default function ShoppingCart() {
  const navigate = useNavigate();
  const { cartItems, setCartItems } = useCheckout();
  const [coupon, setCoupon] = useState("");

  const increase = (id) =>
    setCartItems((s) =>
      s.map((it) => (it.id === id ? { ...it, qty: it.qty + 1 } : it))
    );
  const decrease = (id) =>
    setCartItems((s) =>
      s.map((it) =>
        it.id === id && it.qty > 1 ? { ...it, qty: it.qty - 1 } : it
      )
    );
  const remove = (id) => setCartItems((s) => s.filter((it) => it.id !== id));

  // reactive totals
  const rowSubtotals = cartItems.map((it) => it.price * it.qty);
  const subtotal = rowSubtotals.reduce((sum, n) => sum + n, 0);
  const shipping = cartItems.length ? 500 : 0; // F
  const total = subtotal + shipping; // G

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        購物車
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="medium" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell width={80}>商品</TableCell>
              <TableCell>品名／描述</TableCell>
              <TableCell align="center" width={160}>
                數量
              </TableCell>
              <TableCell align="right" width={160}>
                單價（未稅）
              </TableCell>
              <TableCell align="right" width={160}>
                小計（未稅）
              </TableCell>
              <TableCell align="center" width={120}>
                操作
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {cartItems.map((it, idx) => (
              <TableRow key={it.id}>
                {/* A: product image */}
                <TableCell>
                  <Box
                    component="img"
                    src={it.img}
                    alt={it.name}
                    sx={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 1,
                    }}
                  />
                </TableCell>

                {/* A: name/desc */}
                <TableCell>
                  <Typography fontWeight={600}>{it.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {it.desc}
                  </Typography>
                </TableCell>

                {/* B: qty (decrease / value / add) */}
                <TableCell align="center">
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={1}
                  >
                    <IconButton
                      size="small"
                      onClick={() => decrease(it.id)}
                      disabled={it.qty === 1}
                      aria-label="decrease"
                    >
                      <Remove />
                    </IconButton>
                    <Typography sx={{ minWidth: 24, textAlign: "center" }}>
                      {it.qty}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => increase(it.id)}
                      aria-label="increase"
                    >
                      <Add />
                    </IconButton>
                  </Stack>
                </TableCell>

                {/* C: unit price */}
                <TableCell align="right">{yen(it.price)}</TableCell>

                {/* NEW: row subtotal (reactive) */}
                <TableCell align="right">{yen(rowSubtotals[idx])}</TableCell>

                {/* D: remove */}
                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={() => remove(it.id)}
                    aria-label="remove"
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {cartItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  購物車目前是空的
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Coupon */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Stack
          spacing={2}
          alignItems="flex-end"
          sx={{ width: { xs: "100%", sm: 460 } }} // control the width of the right column
        >
          {/* Coupon row */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography>優惠券代碼</Typography>
            <TextField
              size="small"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              placeholder="輸入代碼"
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" disabled={!coupon.trim()}>
              適用的
            </Button>
          </Stack>

          {/* Totals */}
          <Box sx={{ textAlign: "right", width: "100%" }}>
            <Typography variant="body1">
              運費（不含稅）：{yen(shipping)}
            </Typography>
            <Typography variant="h6" sx={{ mt: 1, fontWeight: "bold" }}>
              總金額（含稅）：{yen(total)}
            </Typography>
          </Box>

          {/* Checkout button */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/checkout/address")}
            disabled={cartItems.length === 0}
            sx={{ alignSelf: "flex-end" }}
          >
            前往填寫地址
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
